import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Wallet, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Analytics } from '@/services/analytics.service';
import { useGroups } from '@/hooks/useGroups';
import {
  useWalletConnection,
  useWalletMappings,
  useConnectWallet,
  useDisconnectWallet,
  useTestWalletConnection,
  useTriggerWalletSync,
  useWalletAccounts,
  useCreateWalletMapping,
  useUpdateWalletMapping,
  useDeleteWalletMapping,
  usePendingTransactionCount,
} from '@/hooks/useWallet';
import { WalletConnectionCard } from '@/components/wallet/WalletConnectionCard';
import { WalletAccountMappingList } from '@/components/wallet/WalletAccountMappingList';
import { useTranslation } from 'react-i18next';

export function WalletPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [testResult, setTestResult] = useState<{ valid: boolean; error?: string } | null>(null);

  // Queries
  const { data: connection, isLoading: connectionLoading } = useWalletConnection();
  const { data: mappings = [] } = useWalletMappings();
  const { data: groups = [] } = useGroups();
  const { data: pendingCount = 0 } = usePendingTransactionCount();
  const accountsQuery = useWalletAccounts(!!connection);
  const accounts = accountsQuery.data?.accounts ?? [];

  // Mutations
  const connectMutation = useConnectWallet();
  const disconnectMutation = useDisconnectWallet();
  const testMutation = useTestWalletConnection();
  const syncMutation = useTriggerWalletSync();
  const createMappingMutation = useCreateWalletMapping();
  const updateMappingMutation = useUpdateWalletMapping();
  const deleteMappingMutation = useDeleteWalletMapping();

  const handleConnect = async (token: string) => {
    try {
      const result = await connectMutation.mutateAsync(token);
      if (result.entitlement_status) {
        // Token saved but the Wallet account is not entitled to the API: the
        // connection card shows the Premium / API-consent call to action.
        Analytics.track({ name: 'Wallet Entitlement Blocked', properties: { code: result.entitlement_status, source: 'connect' } });
        setTestResult(null);
        return;
      }
      // Auto-test the new token so user gets immediate feedback
      const testResult = await testMutation.mutateAsync();
      setTestResult(testResult);
    } catch {
      // Error handled by React Query
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync();
      setTestResult(null);
    } catch {
      // Error handled by React Query
    }
  };

  const handleTest = async () => {
    try {
      const result = await testMutation.mutateAsync();
      setTestResult(result);
      if (!result.valid && result.entitlement_status) {
        Analytics.track({ name: 'Wallet Entitlement Blocked', properties: { code: result.entitlement_status, source: 'test' } });
      }
    } catch {
      setTestResult({ valid: false, error: 'Test failed' });
    }
  };

  const handleSync = async () => {
    try {
      await syncMutation.mutateAsync();
    } catch {
      // Error handled by React Query
    }
  };

  if (connectionLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  const groupOptions = (groups ?? []).map(g => ({
    id: g.id,
    name: g.name,
    currency_code: g.currency_code,
    color: g.color ?? null,
  }));

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wallet className="h-6 w-6 text-text-quaternary" />
          <h1 className="text-2xl font-bold text-text-primary">{t('web:common.walletImport')}</h1>
        </div>

        {pendingCount > 0 ? (
          <button
            onClick={() => navigate('/wallet/transactions')}
            className="flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/30 px-4 py-2 text-sm font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
          >
            {t('web:walletPage.pendingTransactions', { count: pendingCount })}
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : connection ? (
          <button
            onClick={() => navigate('/wallet/transactions')}
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-surface-secondary transition-colors"
          >
            {t('web:walletPage.viewTransactions')}
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {/* Error displays */}
      {connectMutation.isError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-400">
          {t('web:walletPage.failedToConnect', { message: (connectMutation.error as Error)?.message ?? t('web:common.unknownError') })}
        </div>
      )}

      {syncMutation.isSuccess && (
        <div className="rounded-lg bg-green-50 dark:bg-green-900/30 p-3 text-sm text-green-700 dark:text-green-400">
          {(() => {
            const { inserted, updated, auto_approved, errors, sync_in_progress } = syncMutation.data;
            const parts: string[] = [];
            if (inserted > 0) parts.push(t('web:walletPage.newTransactions', { count: inserted }));
            if (updated > 0) parts.push(t('web:walletPage.updatedCount', { count: updated }));
            if (auto_approved > 0) parts.push(t('web:walletPage.autoApprovedCount', { count: auto_approved }));
            return (
              <>
                {t('web:walletPage.syncComplete')} {parts.length > 0 ? parts.join('. ') + '.' : t('web:walletPage.noNewOrChangedTransactionsFound')}
                {errors.length > 0 && (
                  <span className="block mt-1 text-amber-700 dark:text-amber-400">
                    {t('web:walletPage.errorsOccurred', { count: errors.length })}
                  </span>
                )}
                {sync_in_progress && (
                  <span className="block mt-1 text-amber-700 dark:text-amber-400">
                    {t('web:walletAccountMappingList.walletStillSyncing')}
                  </span>
                )}
              </>
            );
          })()}
        </div>
      )}

      {testResult && (
        <div className={`rounded-lg p-3 text-sm ${testResult.valid ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
          {testResult.valid ? t('web:walletPage.connectionIsValid') : t('web:walletPage.connectionTestFailed', { error: testResult.error })}
        </div>
      )}

      {/* Connection card */}
      <WalletConnectionCard
        connection={connection ?? null}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        onSync={handleSync}
        onTest={handleTest}
        isConnecting={connectMutation.isPending}
        isSyncing={syncMutation.isPending}
        isTesting={testMutation.isPending}
      />

      {/* Account mappings (only shown when connected) */}
      {connection && (
        <WalletAccountMappingList
          accounts={accounts}
          mappings={mappings}
          groups={groupOptions}
          onRefreshAccounts={() => accountsQuery.refetch()}
          isRefreshingAccounts={accountsQuery.isFetching}
          syncInProgress={accountsQuery.data?.sync_in_progress ?? false}
          connectionId={connection.id}
          userId={user!.id}
          onCreateMapping={data => createMappingMutation.mutate(data)}
          onUpdateMapping={(mappingId, updates) =>
            updateMappingMutation.mutate({ mappingId, updates })
          }
          onDeleteMapping={mappingId => deleteMappingMutation.mutate(mappingId)}
        />
      )}

      {/* Info section */}
      <div className="rounded-xl bg-surface-secondary p-6 ring-1 ring-border-primary">
        <h3 className="text-sm font-semibold text-text-primary">{t('profile:walletInfo.howItWorks.section')}</h3>
        <ol className="mt-3 space-y-2 text-sm text-text-secondary">
          <li className="flex gap-2">
            <span className="font-medium text-text-primary">1.</span>
            {t('web:walletPage.connectYourWalletByBudgetbakersAccount')}
          </li>
          <li className="flex gap-2">
            <span className="font-medium text-text-primary">2.</span>
            {t('web:walletPage.mapYourBankAccountsToSharecost')}
          </li>
          <li className="flex gap-2">
            <span className="font-medium text-text-primary">3.</span>
            {t('web:walletPage.transactionsAreSyncedAutomaticallyEvery6')}
          </li>
          <li className="flex gap-2">
            <span className="font-medium text-text-primary">4.</span>
            {t('web:walletPage.reviewPendingTransactionsAndApproveThem')}
          </li>
        </ol>
      </div>
    </div>
  );
}
